import { MESApiService, spParameter } from "@dexteel/mesf-core";
import moment from "moment";
import type { PartInfo } from "../../../shared/models";
import type { PartId } from "../models";
import { SPARES_CONTAINER_TO_PART_TYPE } from "../models";
import type {
  PartsTreeRowAPI,
  PartsTreeResponse,
  PartDetailsMasterAPI,
  PartDetailsMetricsAPI,
  PartDetailsHistoryAPI,
  PartDetailsResponse,
} from "../models/api/types";

// ============================================
// Repository Functions
// ============================================

/**
 * Get the parts tree for a specific root part.
 * Calls EQP.GetPartsTree directly — same approach as Roof.
 * Returns a single result set for the given root.
 */
export const getPartsTree = async (rootPartId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "RootPartId", value: rootPartId });
  parameters.push({ name: "Timestamp", value: null });

  const response = await apiService.callV2(
    "[RBD].[EQPGetPartsTree]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load parts tree",
    };
  }

  const data = response.data as PartsTreeResponse;

  return {
    ok: true,
    data,
  };
};

/**
 * Get details for a specific part (includes metrics and history)
 * Calls RBD.GetSidewallPartDetails stored procedure
 */
export const getPartDetails = async (
  partId: number | null,
  parentPartId?: number | null,
  positionId?: number | null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Timestamp", value: null });
  parameters.push({ name: "ParentPartId", value: parentPartId ?? null });
  parameters.push({ name: "PositionId", value: positionId ?? null });

  const response = await apiService.callV2(
    "[RBD].[EQPGetSidewallPartDetails]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load part details",
    };
  }

  const data = response.data as PartDetailsResponse;

  // Process Master data dates (result set 0)
  data.tables[0].rows = data.tables[0].rows.map((row) => ({
    ...row,
    InstalledOn: row.InstalledOn ? moment.utc(row.InstalledOn).toDate() : null,
    ResetOn: row.ResetOn ? moment.utc(row.ResetOn).toDate() : null,
    UninstalledOn: row.UninstalledOn
      ? moment.utc(row.UninstalledOn).toDate()
      : null,
  }));

  // Process History data dates (result set 2)
  data.tables[2].rows = data.tables[2].rows.map((row) => ({
    ...row,
    Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
    PreviousTask: row.PreviousTask
      ? moment.utc(row.PreviousTask).toDate()
      : null,
  }));

  return {
    ok: true,
    data,
  };
};

/**
 * Set a sidewall assembly online.
 * Uses the Sidewall-specific wrapper for granular permission control.
 */
export const setSidewallOnline = async (
  sidewallPartId: number,
  parentPartId: number,
  operatorNumber: number | null = null,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "SidewallPartId", value: sidewallPartId });
  parameters.push({ name: "ParentPartId", value: parentPartId });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPSidewallSetOnline]", parameters);
};

/**
 * Set a sidewall assembly offline (moves to spares).
 * Uses the Sidewall-specific wrapper for granular permission control.
 */
export const setSidewallOffline = async (
  sidewallPartId: number,
  operatorNumber: number | null = null,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "SidewallPartId", value: sidewallPartId });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPSidewallSetOffline]", parameters);
};

/**
 * Update part details (Make, Model, Vendor, SerialNumber, Comments)
 * Calls RBD.UpsertPartDetails stored procedure
 */
export const upsertSidewallPartDetails = async (
  partId: number,
  partInfo: PartInfo,
  comments: string | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Make", value: partInfo.Make || null });
  parameters.push({ name: "Model", value: partInfo.Model || null });
  parameters.push({ name: "Vendor", value: partInfo.Vendor || null });
  parameters.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  parameters.push({ name: "Comments", value: comments || null });

  return await apiService.callV2("[RBD].[UpsertPartDetails]", parameters);
};

// movePart and createPart removed — replaced by EQPInstallPart (atomic)

/**
 * INSTALL PART - Unified: handles both new part creation and existing spare install
 *
 * Uses RBD.EQPInstallPart which wraps CreatePart + UpsertTask + UpsertPartDetails
 * in a transaction with controlled timestamps.
 */
export const installSidewallPart = async (
  sidewallPartId: number,
  positionId: number,
  partTypeId: number,
  partInfo: PartInfo,
  comments: string | null = null,
  userId: number | null = null,
  operatorNumber?: number,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "IsNewPart", value: true });
  parameters.push({ name: "PartTypeId", value: partTypeId });
  parameters.push({ name: "PartName", value: partInfo.SerialNumber || null });
  parameters.push({ name: "ParentPartId", value: sidewallPartId });
  parameters.push({ name: "PositionId", value: positionId });
  parameters.push({ name: "Make", value: partInfo.Make || null });
  parameters.push({ name: "Model", value: partInfo.Model || null });
  parameters.push({ name: "Vendor", value: partInfo.Vendor || null });
  parameters.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPInstallPart]", parameters);
};

/**
 * INSTALL EXISTING SPARE PART
 *
 * Uses RBD.EQPInstallPart with @IsNewPart = 0.
 */
export const installExistingSidewallPart = async (
  existingPartId: number,
  sidewallPartId: number,
  positionId: number,
  comments: string | null = null,
  userId: number | null = null,
  operatorNumber?: number,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "IsNewPart", value: false });
  parameters.push({ name: "PartId", value: existingPartId });
  parameters.push({ name: "ParentPartId", value: sidewallPartId });
  parameters.push({ name: "PositionId", value: positionId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPInstallPart]", parameters);
};

/**
 * UNINSTALL PART - Uses RBD.EQPUninstallPart with chronological validation
 */
export const uninstallSidewallPart = async (
  partId: number,
  operatorNumber?: number,
  comments: string | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPUninstallPart]", parameters);
};

/**
 * REPAIR PART - Uses RBD.EQPRepairPart with chronological validation
 */
export const repairSidewallPart = async (
  partId: number,
  operatorNumber?: number,
  comments: string | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPRepairPart]", parameters);
};

/**
 * CREATE SPARE PART
 */
export const createSparePart = async (
  containerId: number,
  partInfo: PartInfo,
  userId: number | null = null,
) => {
  const apiService = new MESApiService();

  const partTypeId = SPARES_CONTAINER_TO_PART_TYPE[containerId as PartId];
  if (!partTypeId) {
    return { ok: false, message: "Invalid container ID" };
  }

  const partName = partInfo.SerialNumber || null;

  const createParams: spParameter[] = [];
  createParams.push({ name: "PartTypeId", value: partTypeId });
  createParams.push({ name: "PartName", value: partName });
  createParams.push({ name: "UserId", value: userId });

  const createResponse = await apiService.callV2(
    "[RBD].[EQPCreatePart]",
    createParams,
  );

  if (!createResponse.ok) {
    return createResponse;
  }

  const newPartId = createResponse.data?.returnValue;

  if (!newPartId) {
    return { ok: false, message: "Failed to get new part ID" };
  }

  // Set part details
  const detailsParams: spParameter[] = [];
  detailsParams.push({ name: "PartId", value: newPartId });
  detailsParams.push({ name: "Make", value: partInfo.Make || null });
  detailsParams.push({ name: "Model", value: partInfo.Model || null });
  detailsParams.push({ name: "Vendor", value: partInfo.Vendor || null });
  detailsParams.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  detailsParams.push({ name: "InstallDate", value: null });
  detailsParams.push({ name: "Comments", value: null });

  return await apiService.callV2("[RBD].[UpsertPartDetails]", detailsParams);
};

/**
 * GET TASK METADATA
 */
export const getTaskMetadata = async (taskId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "TaskId", value: taskId });

  const response = await apiService.callV2(
    "[RBD].[EQPGetTaskMetadata]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load task metadata",
    };
  }

  const data = response.data;
  if (data?.tables?.[0]?.rows) {
    data.tables[0].rows = data.tables[0].rows.map(
      (row: { CreatedAt: string | Date }) => ({
        ...row,
        CreatedAt: row.CreatedAt ? moment.utc(row.CreatedAt).toDate() : null,
      }),
    );
  }

  return {
    ok: true,
    data,
  };
};

/**
 * ADD TASK METADATA
 */
export const addTaskMetadata = async (
  taskId: number,
  notes: string,
  userId: number | null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "TaskId", value: taskId });
  parameters.push({ name: "Notes", value: notes });
  parameters.push({ name: "UserId", value: userId });

  return await apiService.callV2("[RBD].[EQPAddTaskMetadata]", parameters);
};

/**
 * DELETE SPARE PART
 * Soft deletes a part that is in a spares container
 * Part must be in a default array (spares) to be deleted
 */
export const deleteSparePart = async (
  partId: number,
  userId: number | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "UserId", value: userId });

  return await apiService.callV2("[RBD].[DeleteSparePart]", parameters);
};
